import { useCallback, useState } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import WatchlistSectionView from "./WatchlistSectionView";
import { useKanbanColors } from "@/features/player-kanban/hooks/useKanbanColors";

/**
 *
 */
interface Watchlist {
  id: string;
  name: string;
}

/**
 *
 */
interface WatchlistSectionContainerProps {
  selectedWatchlist: Watchlist | null;
  watchlists: Watchlist[];
  isLoadingWatchlists?: boolean;
  watchlistError?: string | null;
  onWatchlistChange: (watchlistId: string) => void;
}

/**
 * WatchlistSectionContainer - Container Component
 * Manages watchlist selection state and dropdown open/close behavior.
 * Uses useSafeAreaInsets hook to get bottom inset for proper safe area handling.
 * @param root0 - The component props
 * @param root0.selectedWatchlist - Currently selected watchlist or null if none selected
 * @param root0.watchlists - Array of all available watchlists
 * @param root0.isLoadingWatchlists - Whether watchlists are currently being loaded
 * @param root0.watchlistError - Error message if watchlist loading failed
 * @param root0.onWatchlistChange - Callback when a watchlist is selected
 */
const WatchlistSectionContainer = ({
  selectedWatchlist,
  watchlists,
  isLoadingWatchlists = false,
  watchlistError = null,
  onWatchlistChange,
}: WatchlistSectionContainerProps) => {
  const colors = useKanbanColors();
  const { bottom: bottomInset } = useSafeAreaInsets();
  const [isOpen, setIsOpen] = useState(false);

  const handleValueChange = useCallback(
    (watchlistId: string) => {
      onWatchlistChange(watchlistId);
    },
    [onWatchlistChange]
  );

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <WatchlistSectionView
      selectedWatchlist={selectedWatchlist}
      watchlists={watchlists}
      isLoadingWatchlists={isLoadingWatchlists}
      watchlistError={watchlistError}
      colors={colors}
      onValueChange={handleValueChange}
      isOpen={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      bottomInset={bottomInset}
    />
  );
};

export default WatchlistSectionContainer;
